import { buildBrowseHref, readCanonicalBrowseState } from '@/lib/browse-state';

export type GeographySection =
  | 'home'
  | 'classes'
  | 'creators'
  | 'calendar'
  | 'community'
  | 'updates'
  | 'locations'
  | 'search'
  | 'unknown';

export interface GeographySelection {
  regionSlug: string;
  localitySlug?: string;
}

export interface ResolvedGeographyLocation extends GeographySelection {
  section: GeographySection;
}

const SECTION_SEGMENTS = new Set([
  'classes',
  'creators',
  'calendar',
  'community',
  'updates',
  'locations',
]);

function normalizePathname(pathname: string) {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || '/';
}

export function formatGeographySlugLabel(slug: string) {
  return slug
    .split('-')
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export function buildGeographyBasePath(regionSlug: string, localitySlug?: string) {
  return localitySlug ? `/regions/${regionSlug}/${localitySlug}` : `/regions/${regionSlug}`;
}

export function resolveGeographySelectionFromUrl(
  pathname: string,
  search: string,
  fallback: GeographySelection
): ResolvedGeographyLocation {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === '/search') {
    const browseState = readCanonicalBrowseState(search);

    return {
      regionSlug: browseState.region ?? fallback.regionSlug,
      localitySlug: browseState.locality ?? fallback.localitySlug,
      section: 'search',
    };
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  if (segments[0] !== 'regions' || segments.length < 2) {
    return {
      regionSlug: fallback.regionSlug,
      localitySlug: fallback.localitySlug,
      section: 'unknown',
    };
  }

  const regionSlug = segments[1];

  if (segments.length === 2) {
    return { regionSlug, section: 'home' };
  }

  const thirdSegment = segments[2];
  if (SECTION_SEGMENTS.has(thirdSegment)) {
    return { regionSlug, section: thirdSegment as GeographySection };
  }

  if (segments.length === 3) {
    return {
      regionSlug,
      localitySlug: thirdSegment,
      section: 'home',
    };
  }

  const fourthSegment = segments[3];
  return {
    regionSlug,
    localitySlug: thirdSegment,
    section: SECTION_SEGMENTS.has(fourthSegment) ? (fourthSegment as GeographySection) : 'home',
  };
}

export function buildScopedGeographyHref(
  pathname: string,
  search: string,
  target: GeographySelection,
  fallback: GeographySelection
) {
  const resolved = resolveGeographySelectionFromUrl(pathname, search, fallback);

  if (resolved.section === 'search') {
    const browseState = readCanonicalBrowseState(search);
    browseState.region = target.regionSlug;
    browseState.locality = target.localitySlug;
    return buildBrowseHref('/search', browseState);
  }

  const basePath = buildGeographyBasePath(target.regionSlug, target.localitySlug);
  const appendSection = resolved.section !== 'home' && resolved.section !== 'unknown';
  const scopedPath = appendSection ? `${basePath}/${resolved.section}` : basePath;
  const params = new URLSearchParams(search);

  params.delete('region');
  params.delete('locality');

  const query = params.toString();
  return `${scopedPath}${query ? `?${query}` : ''}`;
}
